'use client';

import { useEffect, useRef } from 'react';

import { Box, Typography } from '@mui/material';
import cx from 'clsx';
import NextImage from 'next/image';

import { Image } from '@/types/image.type';
import { Video } from '@/types/video.type';
import useBreakpoint from '@/utils/hooks/useBreakpoint';
import useIntersectionObserver from '@/utils/hooks/useIntersectionObserver';
import useRevealAnimation from '@/utils/hooks/useRevealAnimation';

import styles from './FeatureCard.module.scss';

interface FeatureCardProps {
  image?: Image;
  video?: Video;
  imageMobile?: Image;
  title: string;
  description: string;
  index: number;
}

const FeatureCard = ({ image, video, imageMobile, title, description, index }: FeatureCardProps) => {
  const { isMobile } = useBreakpoint();
  const triggerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const mediaWrapperRef = useRef<HTMLDivElement>(null);

  useRevealAnimation({ triggerRef });

  const shouldShowVideo = video?.src;
  const imageSrc = isMobile && imageMobile?.src ? imageMobile.src : image?.src;

  const isInViewport = useIntersectionObserver(mediaWrapperRef, {
    threshold: 0.2,
    triggerOnce: false,
    rootMargin: '100px 0px',
  });

  useEffect(() => {
    if (!shouldShowVideo || !videoRef.current) return undefined;

    const videoElement = videoRef.current;
    let canPlayHandler: (() => void) | null = null;

    if (isInViewport) {
      const playPromise = videoElement.play();

      if (playPromise !== undefined) {
        playPromise.catch(() => {
          canPlayHandler = () => {
            videoElement.play().catch(() => {});
          };

          videoElement.addEventListener('canplay', canPlayHandler, { once: true });
        });
      }
    } else {
      videoElement.pause();
    }

    return () => {
      if (canPlayHandler) {
        videoElement.removeEventListener('canplay', canPlayHandler);
      }
    };
  }, [isInViewport, shouldShowVideo]);

  return (
    <Box
      ref={triggerRef}
      className={cx(styles.container, {
        [styles.right]: index % 2 === 0,
        [styles.left]: index % 2 !== 0,
      })}
    >
      <Box
        ref={mediaWrapperRef}
        className={cx(styles.imageWrapper, { [styles.right]: index % 2 === 0, [styles.left]: index % 2 !== 0 })}
      >
        {shouldShowVideo ? (
          <video
            ref={videoRef}
            muted
            loop
            playsInline
            preload="metadata"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
            aria-label={video.alt || title}
          >
            <source src={video.src} type="video/mp4" />
          </video>
        ) : (
          imageSrc && (
            <NextImage
              src={imageSrc}
              alt={image?.alt || title}
              fill
              style={{ objectFit: 'cover' }}
              unoptimized={imageSrc.endsWith('.gif')}
            />
          )
        )}
      </Box>
      <Typography component="h3" variant="h5" className={styles.title}>
        {title}
      </Typography>
      <Typography variant="body1" className={styles.description}>
        {description}
      </Typography>
    </Box>
  );
};

export default FeatureCard;
